import accept from "attr-accept";
import React, {ReactElement} from "react";
import {useActiveLicence} from "./useActiveLicence";

type UseUploadErrorsProps = {
    selectedFiles: File[];
    machineType?: string;
    acceptedFileType: string;
}

export const useUploadErrors = ({
                                    selectedFiles,
                                    machineType,
                                    acceptedFileType
                                }: UseUploadErrorsProps): { errors: ReactElement<any, any>[], hasError: boolean } => {

    const [hasActiveLicence,,isTrial] = useActiveLicence();

    const errors: ReactElement<any, any>[] = [];

    const hasSelectedFiles = selectedFiles.length > 0;

    if (!hasActiveLicence) {
        errors.push(
            <p>
                No active license found. Order a new licence{" "}
                <a href="https://mabriteccentral.com/order" rel="noreferrer">
                    here
                </a>
                .
            </p>
        );
    }

    if (
        machineType === "bruker" &&
        !selectedFiles
            .map((file: File) => accept(file, acceptedFileType))
            .every(Boolean)
    ) {
        errors.push(
            <p>For Bruker Biotyper only files of type .zip are allowed.</p>
        );
    }

    if (
        machineType === "clover" &&
        !selectedFiles
            .map((file: File) => accept(file, acceptedFileType))
            .every(Boolean)
    ) {
        errors.push(
            <p>For Clover BioSoft only files of type .zip are allowed.</p>
        );
    }

    if (
        machineType === "vitekMSMzml" &&
        !selectedFiles
            .map((file: File) => accept(file, acceptedFileType))
            .every(Boolean)
    ) {
        errors.push(
            <p>For Vitek MS (mzml) only files of type .mzml are allowed.</p>
        );
    }

    if (
        machineType === "shimadzuAscii" &&
        !selectedFiles
            .map((file: File) => accept(file, acceptedFileType))
            .every(Boolean)
    ) {
        errors.push(
            <p>
                For Shimadzu 8020 / 8030 (ascii) only files of type .txt are allowed.
            </p>
        );
    }

    if (
        machineType === "vitekMSAscii" &&
        !selectedFiles
            .map((file: File) => accept(file, acceptedFileType))
            .every(Boolean)
    ) {
        errors.push(
            <p>For Vitek MS (ascii) only files of type .txt are allowed.</p>
        );
    }

    if(hasSelectedFiles && !machineType) {
        errors.push(<p>Please select your machine.</p>);
    }

    if(hasSelectedFiles &&
        selectedFiles
            .map((file) => file.size > 30 * 1024 * 1024)
            .every(Boolean)) {
        errors.push(<p>One or more file(s) exceed 30MB.</p>);
    }

    if(selectedFiles.length > 500) {
        errors.push(<p>Maximum 500 files allowed.</p>);
    }

    const isShimadzuOrVitekMSAscii =
        machineType === "shimadzuAscii" || machineType === "vitekMSAscii";

    const isFileTooBigForShimadzuOrVitekMSAscii = selectedFiles.length !== 0 &&
        selectedFiles
            .map((file) => file.size > 200 * 1024)
            .every(Boolean);

    if (isShimadzuOrVitekMSAscii && isFileTooBigForShimadzuOrVitekMSAscii) {
        errors.push(
            <p>
                For Shimadzu 8020 / 8030 (ascii) and Vitek MS Prime (ascii) files must not exceed 200kb.
            </p>
        );
    }

    if(isTrial && hasSelectedFiles && selectedFiles.length > 20) {
        errors.push(
            <p>
                You're currently using a trial license, which allows up to 20 files per upload.
                To upload more files, please purchase a full license <a href="https://mabriteccentral.com/order" rel="noreferrer">here</a>.
            </p>
        );
    }

    return {
        errors: errors,
        hasError: errors.length > 0
    };
}